import React, { useState, useEffect } from 'react';
import { Field } from 'formik';
import axios from 'axios';

const PaiementADAR = ({ formikProps }) => {
  const { values, errors, touched, setFieldValue } = formikProps;
  const [parametres, setParametres] = useState(null);
  const [loading, setLoading] = useState(true);

  // Récupérer les paramètres de tarifs pour ADAR
  useEffect(() => {
    const fetchParametres = async () => {
      try {
        const organisme = 'ADAR';
        const annee = values.annee;
        
        if (!annee) return;

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/parametres/${organisme}/${annee}`
        );
        setParametres(response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des paramètres:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchParametres();
  }, [values.annee]);

  // Calculer le montant pour ADAR : 70€ base + 0,5€ par ruche
  const calculateMontantADAR = () => {
    if (!parametres || !parametres.tarifs || !parametres.tarifs.ADAR) {
      // Valeurs par défaut si les paramètres ne sont pas encore chargés
      const baseADAR = 70;
      const tarifParRuche = 0.5;
      const nombreRuches = values.informationsApicoles?.nombreRuches || 0;
      return baseADAR + (nombreRuches * tarifParRuche);
    }
    
    const cotisationBase = parametres.tarifs.ADAR.base;
    const nombreRuches = values.informationsApicoles?.nombreRuches || 0;
    const cotisationRuches = nombreRuches * parametres.tarifs.ADAR.cotisationParRuche;
    
    return cotisationBase + cotisationRuches;
  };

  const getTotalMontant = () => {
    return calculateMontantADAR();
  };

  // Mettre à jour le montant dans le formulaire quand il change
  useEffect(() => {
    if (!loading) {
      const montant = getTotalMontant();
      setFieldValue('paiement.montant', montant);
      // Pour ADAR, seul le paiement en ligne est disponible
      setFieldValue('paiement.typePaiement', 'en_ligne');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, parametres, values.informationsApicoles?.nombreRuches]);

  const getOrganismeFullName = () => {
    return 'Association pour le Développement de l\'Apiculture à l\'île de la Réunion';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="text-lg text-gray-600">Chargement des informations de paiement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="text-center">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Paiement de votre adhésion
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Récapitulatif du montant de votre cotisation ADAR
        </p>
      </div>

      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="space-y-4">
          <div className="border-b border-gray-200 pb-4">
            <h4 className="text-base font-medium text-gray-900">
              Adhésion {getOrganismeFullName()} - Année {values.annee}
            </h4>
          </div>

          {/* Récapitulatif du montant */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Cotisation de base annuelle</span>
                <span className="font-medium text-gray-900">
                  {parametres?.tarifs?.ADAR?.base?.toFixed(2) || '70.00'} €
                </span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">
                  Cotisation par ruche ({values.informationsApicoles?.nombreRuches || 0} ruches × {parametres?.tarifs?.ADAR?.cotisationParRuche?.toFixed(2) || '0.50'} €)
                </span>
                <span className="font-medium text-gray-900">
                  {((values.informationsApicoles?.nombreRuches || 0) * (parametres?.tarifs?.ADAR?.cotisationParRuche || 0.5)).toFixed(2)} €
                </span>
              </div>
              <div className="border-t border-gray-300 pt-2 mt-2">
                <div className="flex justify-between">
                  <span className="text-base font-semibold text-gray-900">Total à régler</span>
                  <span className="text-xl font-bold text-indigo-600">
                    {getTotalMontant().toFixed(2)} €
                  </span>
                </div>
              </div>
            </div>

            {/* Détail du calcul */}
            <div className="mt-3 pt-3 border-t border-gray-200 text-sm text-gray-600">
              <p>• Cotisation de base : {parametres?.tarifs?.ADAR?.base?.toFixed(2) || '70.00'} €</p>
              <p>• Cotisation par ruche : {parametres?.tarifs?.ADAR?.cotisationParRuche?.toFixed(2) || '0.50'} € × {values.informationsApicoles?.nombreRuches || 0} ruches</p>
            </div>
          </div>

          {/* Mode de paiement - Uniquement en ligne pour ADAR */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Mode de paiement <span className="text-red-500">*</span>
            </label>

            <div className="space-y-3">
              <div className="relative flex items-start bg-indigo-50 border-2 border-indigo-500 rounded-lg p-4">
                <div className="flex items-center h-5">
                  <Field
                    type="radio"
                    id="paiement_en_ligne"
                    name="paiement.typePaiement"
                    value="en_ligne"
                    checked={true}
                    disabled={true}
                    className="h-4 w-4 text-indigo-600 border-gray-300 focus:ring-indigo-500"
                  />
                </div>
                <div className="ml-3 flex-1">
                  <label htmlFor="paiement_en_ligne" className="font-medium text-gray-900 cursor-pointer">
                    Paiement en ligne par carte bancaire
                  </label>
                  <p className="text-sm text-gray-600 mt-1">
                    Paiement sécurisé via Stripe. Vous serez redirigé vers la page de paiement après validation de votre demande par l'administration.
                  </p>
                </div>
              </div>
            </div>

            {errors.paiement?.typePaiement && touched.paiement?.typePaiement && (
              <p className="mt-2 text-sm text-red-600">{errors.paiement.typePaiement}</p>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default PaiementADAR;
